// Router + shell. Mounts public pages or admin based on the hash route.

import React from "react";
import { useSite } from "./store.jsx";
import { useToast } from "./components.jsx";
import { CenteredHeader, MinimalFooter, Home, Portfolio, SectionPage, About, Contact } from "./site.jsx";
import { Admin } from "./admin.jsx";

// Routing notes:
// - Admin lives at the real path /admin so Cloudflare Access can gate it
//   (Access only sees HTTP paths, not the fragment after #).
// - Public pages stay hash-routed so the SPA serves entirely from /.
function parseRoute() {
  const pathname = window.location.pathname;
  if (pathname === "/admin" || pathname.startsWith("/admin/")) {
    const rest = pathname.replace(/^\/admin\/?/, "");
    const parts = rest.split("/").filter(Boolean);
    const adminTab = parts[0] || "sections";
    const adminEditingSection = (parts[0] === "sections" && parts[1]) ? parts[1] : null;
    return { name: "admin", adminTab, adminEditingSection };
  }
  const h = window.location.hash || "#/";
  const path = h.replace(/^#/, "");
  const parts = path.split("/").filter(Boolean);
  if (parts.length === 0) return { name: "home" };
  if (parts[0] === "portfolio") return { name: "portfolio" };
  if (parts[0] === "section")   return { name: "section", sid: parts[1] };
  if (parts[0] === "about")     return { name: "about" };
  if (parts[0] === "contact")   return { name: "contact" };
  return { name: "home" };
}

function useRoute() {
  const [r, setR] = React.useState(parseRoute());
  React.useEffect(() => {
    const fn = () => { setR(parseRoute()); window.scrollTo(0, 0); };
    window.addEventListener("hashchange", fn);
    window.addEventListener("popstate", fn);
    return () => {
      window.removeEventListener("hashchange", fn);
      window.removeEventListener("popstate", fn);
    };
  }, []);
  return r;
}

// Accepts either a hash target ("#/portfolio") or a path target ("/admin/bio").
// Admin uses real paths (Cloudflare Access gate); everything else uses hash.
function go(target) {
  if (target.startsWith("#/admin")) {
    // Back-compat: translate any leftover hash-style admin links to a path nav.
    const sub = target.slice("#/admin".length).replace(/^\//, "");
    const url = "/admin" + (sub ? "/" + sub : "");
    if (window.location.pathname === url) return;
    window.location.assign(url);
    return;
  }
  if (target.startsWith("/admin")) {
    if (window.location.pathname === target) return;
    // pushState keeps us in the same SPA; popstate listener picks up the change.
    window.history.pushState({}, "", target);
    window.dispatchEvent(new PopStateEvent("popstate"));
    return;
  }
  // Public hash route. If we're currently on /admin, fall through to root first.
  if (window.location.pathname !== "/") {
    window.location.assign("/" + (target === "#/" ? "" : target));
    return;
  }
  if (window.location.hash === target) return;
  window.location.hash = target;
}

export default function App() {
  const [state, actions] = useSite();
  const route = useRoute();
  const [toastNode, toast] = useToast();

  // Apply tweaks (loaded from server) to <body>.
  React.useEffect(() => {
    document.body.dataset.direction = state.tweaks.direction;
    document.body.dataset.paper = state.tweaks.paper;
    document.documentElement.style.setProperty("--accent", state.tweaks.accent);
  }, [state.tweaks.direction, state.tweaks.paper, state.tweaks.accent]);

  if (route.name === "admin") {
    return <>
      <Admin state={state} actions={actions} route={route} go={go} toast={toast} />
      {toastNode}
    </>;
  }

  let content = null;
  if (route.name === "home")           content = <Home state={state} go={go} />;
  else if (route.name === "portfolio") content = <Portfolio state={state} go={go} />;
  else if (route.name === "section")   content = <SectionPage state={state} sid={route.sid} go={go} />;
  else if (route.name === "about")     content = <About state={state} />;
  else if (route.name === "contact")   content = <Contact state={state} />;
  else                                 content = <Home state={state} go={go} />;

  return (
    <div className="site-shell">
      <CenteredHeader route={route} go={go} />
      <main className="site-main">{content}</main>
      <MinimalFooter />
      {state.error && <div className="toast" style={{ background: "#a3382a" }}>error: {state.error}</div>}
      {toastNode}
    </div>
  );
}
